import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RENTALS_QUERY_KEYS,
  RENTALS_STALE_TIME,
} from "@/features/rentals/configs/rentalsQueryKeys.config";
import {
  checkoutRentalCart,
  emptyRentalCart,
  getCurrentRentalCart,
  updateRentalCartShippingAddress,
  updateRentalCartSubsidyChoices,
} from "@/features/rentals/services/rentals.service";
import type { CheckoutCartPayload } from "@/features/rentals/types/rentals.type";

type UseRentalCartOptions = {
  /** Court-circuite le fetch quand le feature flag service_catalog
   * est désactivé pour l'org (sinon on tape l'API pour rien). */
  enabled?: boolean
};

/**
 * Source de vérité pour la page panier + le checkout multi-bookings.
 * L'endpoint backend crée le cart à la volée si absent — pas besoin
 * de gérer un état `404` côté front.
 */
export const useRentalCart = ({ enabled = true }: UseRentalCartOptions = {}) =>
  useQuery({
    queryKey: RENTALS_QUERY_KEYS.cart,
    queryFn: getCurrentRentalCart,
    enabled,
    staleTime: RENTALS_STALE_TIME,
  });

/**
 * Mutations cart-level. Toutes invalident le cart + les listes de
 * bookings (le checkout fait sortir les bookings de `cart`, l'empty
 * les cancel — les deux modifient la liste « Mes commandes »).
 */
export const useRentalCartMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.cart });
    queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.myBookings });
    queryClient.invalidateQueries({ queryKey: ["rentals", "booking"] });
    // Vider le panier annule ses réservations : le reste des quotas remonte.
    queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.seasonQuotaUsageAll });
  };

  // Après un checkout réussi, les bookings sortent immédiatement de
  // `pending_checkout` côté serveur (workflow direct) ou bientôt
  // (Paybox IPN). On invalide avec `refetchType: "all"` — y compris
  // les observers inactifs — pour que le `UnifiedCartContext`
  // (toujours monté au-dessus du Router) repasse à jour avant que
  // le benef ne retombe sur /catalogue. Le `removeQueries` historique
  // laissait apparaître l'ancien booking en panier le temps que la
  // refetch arrive — visible sur l'iso ajout d'un nouveau produit
  // juste après checkout.
  const checkout = useMutation({
    mutationFn: ({ cartId, payload }: { cartId: string, payload: CheckoutCartPayload }) =>
      checkoutRentalCart(cartId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: RENTALS_QUERY_KEYS.myBookings,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: RENTALS_QUERY_KEYS.cart,
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["rentals", "booking"] });
    },
  });

  const empty = useMutation({
    mutationFn: (cartId: string) => emptyRentalCart(cartId),
    onSuccess: invalidate,
  });

  // Chaque PATCH porte l'état complet des bascules, construit depuis la
  // query du panier. Les bascules se réactivent dès la fin du PATCH :
  // si la query attend encore son refetch, la bascule suivante repart
  // de l'état d'avant (recette R9, `employee: false` juste après l'avoir
  // passé à `true`). Le PATCH rend le panier complet (même vue que
  // `GET /carts/current`) : on l'écrit tout de suite dans le cache. Un
  // GET encore en vol est annulé avant l'envoi, pour qu'il ne ramène
  // pas l'état précédent par-dessus. Même chose à la réponse : un GET
  // parti pendant le PATCH (montage d'un composant, invalidation) a pu
  // lire le panier avant l'enregistrement et revenir après ;
  // il réécrivait l'ancien état sur l'écran alors que la base avait le
  // nouveau (recette du 08/10, R9). La réponse du PATCH est l'état le plus
  // récent connu : on annule ces lectures avant de l'écrire.
  const updateSubsidyChoices = useMutation({
    mutationFn: ({ cartId, choices }: {
      cartId: string
      choices: { employee?: boolean, beneficiaries?: Record<string, boolean> }
    }) => updateRentalCartSubsidyChoices(cartId, choices),
    onMutate: () => queryClient.cancelQueries({ queryKey: RENTALS_QUERY_KEYS.cart }),
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: RENTALS_QUERY_KEYS.cart });
      queryClient.setQueryData(RENTALS_QUERY_KEYS.cart, data);
      queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.myBookings });
      queryClient.invalidateQueries({ queryKey: ["rentals", "booking"] });
    },
    // Le GET annulé au départ du PATCH ne revient pas : sans relecture,
    // les bascules resteraient sur un état que l'API n'a pas forcément.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: RENTALS_QUERY_KEYS.cart });
    },
  });

  // Attache l'adresse de livraison choisie au cart. À déclencher
  // depuis l'étape « Vos coordonnées » avant `checkout` — le backend
  // snapshot ensuite l'adresse sur chaque booking au moment du
  // checkout (cf. `Core::Catalog::CartCheckoutService`).
  const updateShippingAddress = useMutation({
    mutationFn: ({ cartId, shippingAddressId }: {
      cartId: string
      shippingAddressId: number | null
    }) => updateRentalCartShippingAddress(cartId, shippingAddressId),
    onSuccess: invalidate,
  });

  return {
    checkout, empty, updateSubsidyChoices, updateShippingAddress,
  };
};
